import { observer } from 'mobx-react';
import { FC, useCallback, useMemo } from 'react';
import { ordersRatingStore } from '../store/ordersRatingStore';
import { getFilterOptionsForPicker } from '../helpers';
import OrdersRatingExport from './OrdersRatingExport';
import Calendar from 'src/view/components/Calendar/Calendar';
import { LoadingStatus } from 'src/types/main';
import { SearchInput } from '~/view/components/Controls/Inputs';
import './style.scss';
import { ICalendarSubmitDate } from '~/view/components/Calendar/types';
import Picker from '~/view/components/Controls/Pickers/Picker/Picker';
import { localeStore } from '~/mobx/localesStore';
import { IPickerOption } from '~/view/components/Controls/Pickers/Picker/types';

const OrdersRatingHeader: FC = observer((): JSX.Element => {
  const onSearch = useCallback((search: string | undefined) => {
    if (ordersRatingStore.filter.search && !search) {
      ordersRatingStore.resetSearch();
    } else {
      ordersRatingStore.changeFilters({ search });
    }
  }, []);

  const handleChangeDate = useCallback(({ startDate, endDate }: ICalendarSubmitDate) => {
    if (startDate && endDate) {
      ordersRatingStore.changeFilters({ dateFrom: startDate?.toISOString(), dateTo: endDate?.toISOString() });
    } else if (!startDate && !endDate) {
      ordersRatingStore.resetDate();
    }
  }, []);

  const handleChangeBranches = useCallback((branches: IPickerOption[]) => {
    ordersRatingStore.changeFilters({ branches });
  }, []);

  const handlePickerToggle = useCallback((open: boolean) => {
    if (!open) {
      ordersRatingStore.getOrdersRating();
    } else if (open && ordersRatingStore.filter.branches.length === 0 && ordersRatingStore.availableBranches.length > 1) {
      ordersRatingStore.selectAllBranches();
    }
  }, []);

  const options = useMemo(() => {
    return getFilterOptionsForPicker({ availableBranches: ordersRatingStore.availableBranches, branches: ordersRatingStore.filter.branches });
  }, [ordersRatingStore.availableBranches, ordersRatingStore.filter.branches]);

  return (
    <div className="d-flex flex-row justify-content-between pr-4">
      <div className="d-flex flex-row gap-10">
        <SearchInput
          value={ordersRatingStore.filter.search}
          onChangeValue={onSearch}
          loading={ordersRatingStore.loadingStatus === LoadingStatus.Loading}
          contentClassName="orders-rating-header__search"
        />
        <Calendar
          start_date={ordersRatingStore.filter.dateFrom || undefined}
          end_date={ordersRatingStore.filter.dateTo || undefined}
          changeDate={handleChangeDate}
          fieldClassName="orders-rating-calendar pr-2"
          horizontal="end"
          vertical="bottom"
          defaultView="month"
          goToRangeStartOnSelect={false}
          hideCloseIcon
        />
        <Picker
          placeholder={localeStore.t('reports_all_branches_selected_placeholder')}
          key="branches-picker"
          options={options}
          selectedOptions={ordersRatingStore.filter.branches}
          onSelect={handleChangeBranches}
          onTogglePicker={handlePickerToggle}
          hideResetIcon
          isMulti
          searchable
          className="orders-rating-header__branches-picker mr-2"
        />
      </div>

      <OrdersRatingExport />
    </div>
  );
});

export default OrdersRatingHeader;
